/**
 * Admin PayoutsPage Component
 *
 * Admin page for processing host payout requests.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../layout';
import { useAdminPayouts, PayoutsFilter, PayoutStatus, AdminPayout } from './hooks';

// Format date helper
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Calculate days pending
const daysPending = (date: Date): number => {
  return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
};

export const PayoutsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<PayoutStatus | ''>('requested');
  const [filter, setFilter] = useState<PayoutsFilter>({ status: 'requested' });
  const [selectedPayouts, setSelectedPayouts] = useState<string[]>([]);
  const [processingPayout, setProcessingPayout] = useState<AdminPayout | null>(null);

  const { payouts, stats, isLoading } = useAdminPayouts(filter, 0);

  const handleTabChange = (tab: PayoutStatus | '') => {
    setActiveTab(tab);
    setFilter({ ...filter, status: tab });
    setSelectedPayouts([]);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedPayouts(checked ? payouts.map((p) => p.id) : []);
  };

  const statusColors: Record<PayoutStatus, string> = {
    requested: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payouts</h1>
            <p className="text-sm text-gray-500 mt-1">
              {stats.requested + stats.processing} pending ·{' '}
              ${stats.pendingAmount.toLocaleString()} total
            </p>
          </div>
          {selectedPayouts.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {selectedPayouts.length} selected
              </span>
              <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                Export Selected
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-4">
            {[
              { key: 'requested', label: 'Pending', count: stats.requested, urgent: true },
              { key: 'processing', label: 'Processing', count: stats.processing },
              { key: 'completed', label: 'Completed', count: stats.completed },
              { key: 'rejected', label: 'Rejected', count: stats.rejected },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key as PayoutStatus)}
                className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      tab.urgent && tab.key === activeTab
                        ? 'bg-red-100 text-red-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="relative max-w-md">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ID, host, or reference..."
              value={filter.search || ''}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Payouts Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
              <p className="text-gray-500 mt-2">Loading payouts...</p>
            </div>
          ) : payouts.length === 0 ? (
            <div className="p-8 text-center">
              <DollarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No payouts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="p-4 text-left w-10">
                      <input
                        type="checkbox"
                        checked={
                          selectedPayouts.length > 0 &&
                          selectedPayouts.length === payouts.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                      />
                    </th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">
                      Host
                    </th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">
                      Requested
                    </th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">
                      Amount
                    </th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">
                      Method
                    </th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">
                      Status
                    </th>
                    <th className="p-4 text-left text-sm font-medium text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout) => {
                    const days = daysPending(payout.requestedAt);
                    const isUrgent = payout.status === 'requested' && days >= 3;

                    return (
                      <tr
                        key={payout.id}
                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                          isUrgent ? 'bg-red-50' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedPayouts.includes(payout.id)}
                            onChange={(e) =>
                              setSelectedPayouts((prev) =>
                                e.target.checked
                                  ? [...prev, payout.id]
                                  : prev.filter((id) => id !== payout.id)
                              )
                            }
                            className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                          />
                        </td>

                        {/* Host */}
                        <td className="p-4">
                          <Link
                            to={`/admin/users/${payout.hostId}`}
                            className="flex items-center gap-3 hover:text-primary-600"
                          >
                            <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                              {payout.hostPhoto ? (
                                <img
                                  src={payout.hostPhoto}
                                  alt={payout.hostName}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-primary-100 text-primary-600 font-medium">
                                  {payout.hostName.charAt(0)}
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900">
                                {payout.hostName}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {payout.hostEmail}
                              </p>
                            </div>
                          </Link>
                        </td>

                        {/* Requested */}
                        <td className="p-4">
                          <p className="text-sm text-gray-900">
                            {formatDate(payout.requestedAt)}
                          </p>
                          <p
                            className={`text-xs ${
                              isUrgent ? 'text-red-600 font-medium' : 'text-gray-500'
                            }`}
                          >
                            {days} day{days !== 1 ? 's' : ''} ago
                          </p>
                        </td>

                        {/* Amount */}
                        <td className="p-4">
                          <p className="text-sm font-semibold text-gray-900">
                            ${payout.amount.toFixed(2)}
                          </p>
                          {payout.transferFee > 0 && (
                            <p className="text-xs text-gray-500">
                              Net: ${payout.netAmount.toFixed(2)}
                            </p>
                          )}
                        </td>

                        {/* Method */}
                        <td className="p-4">
                          <p className="text-sm text-gray-900 capitalize">
                            {payout.payoutMethodType.replace('_', ' ')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {payout.payoutMethodType === 'whish_money'
                              ? payout.payoutMethodDetails.phoneNumber
                              : payout.payoutMethodDetails.bankName}
                          </p>
                        </td>

                        {/* Status */}
                        <td className="p-4">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              statusColors[payout.status]
                            }`}
                          >
                            {payout.status}
                          </span>
                          {payout.transactionReference && (
                            <p className="text-xs text-gray-500 mt-1 font-mono">
                              {payout.transactionReference}
                            </p>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="p-4">
                          {payout.status === 'requested' && (
                            <button
                              onClick={() => setProcessingPayout(payout)}
                              className="px-3 py-1.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-600 transition-colors"
                            >
                              Process
                            </button>
                          )}
                          {payout.status === 'processing' && (
                            <button
                              onClick={() => setProcessingPayout(payout)}
                              className="px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors"
                            >
                              Complete
                            </button>
                          )}
                          {(payout.status === 'completed' ||
                            payout.status === 'rejected') && (
                            <button className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900">
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedPayouts.length > 0 && activeTab === 'requested' && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-xl shadow-2xl px-6 py-4 flex items-center gap-6 z-40">
            <span className="text-sm">
              <span className="font-medium">{selectedPayouts.length}</span> payout
              {selectedPayouts.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-3">
              <button className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors">
                Mark Processing
              </button>
              <button className="px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors">
                Mark Complete
              </button>
              <button className="px-3 py-1.5 bg-white text-gray-900 text-sm rounded-lg hover:bg-gray-100 transition-colors">
                Export
              </button>
            </div>
            <button
              onClick={() => setSelectedPayouts([])}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Process Modal */}
        {processingPayout && (
          <ProcessPayoutModal
            payout={processingPayout}
            onClose={() => setProcessingPayout(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Process Payout Modal Component
const ProcessPayoutModal: React.FC<{
  payout: AdminPayout;
  onClose: () => void;
}> = ({ payout, onClose }) => {
  const [step, setStep] = useState<'review' | 'complete'>('review');
  const [transactionRef, setTransactionRef] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleComplete = async () => {
    setIsSubmitting(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Process Payout</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {step === 'review' && (
          <div className="space-y-4">
            {/* Host Info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                {payout.hostPhoto ? (
                  <img
                    src={payout.hostPhoto}
                    alt={payout.hostName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-primary-100 text-primary-600 font-medium">
                    {payout.hostName.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <p className="font-medium">{payout.hostName}</p>
                <p className="text-sm text-gray-500">{payout.hostEmail}</p>
              </div>
            </div>

            {/* Payout Details */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-2">Payout Details</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Amount</dt>
                  <dd className="font-medium">${payout.amount.toFixed(2)}</dd>
                </div>
                {payout.transferFee > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Transfer Fee</dt>
                    <dd>-${payout.transferFee.toFixed(2)}</dd>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t font-semibold">
                  <dt>Net Amount</dt>
                  <dd>${payout.netAmount.toFixed(2)}</dd>
                </div>
              </dl>
            </div>

            {/* Payout Method */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-2">Payout Method</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Type</dt>
                  <dd className="capitalize">
                    {payout.payoutMethodType.replace('_', ' ')}
                  </dd>
                </div>
                {payout.payoutMethodType === 'whish_money' ? (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Phone</dt>
                    <dd className="font-mono">
                      {payout.payoutMethodDetails.phoneNumber}
                    </dd>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Bank</dt>
                      <dd>{payout.payoutMethodDetails.bankName}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">IBAN</dt>
                      <dd className="font-mono text-xs">
                        {payout.payoutMethodDetails.iban}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Beneficiary</dt>
                      <dd>{payout.payoutMethodDetails.beneficiaryName}</dd>
                    </div>
                  </>
                )}
              </dl>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep('complete')}
                className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 'complete' && (
          <div className="space-y-4">
            <p className="text-gray-600 text-center">
              Enter the transaction reference after completing the transfer.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transaction Reference *
              </label>
              <input
                type="text"
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                placeholder="Bank reference or Whish transfer ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep('review')}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleComplete}
                disabled={!transactionRef || isSubmitting}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {isSubmitting ? 'Processing...' : 'Mark Complete'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Icons
const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const DollarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default PayoutsPage;
